import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, History, ChevronLeft, ClipboardCheck, User, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import ReviewHistory from './ReviewHistory';
import {
  approveAssessment,
  denyAssessment,
  approveStudyDraft,
  denyStudyDraft,
} from '../../services/reviewWorkflowApi';

/**
 * Fixed bottom action bar shown while an approver is *reviewing* an in-review
 * entity inside its builder (assessment or study draft).
 *
 * Replaces the old one-click inline Approve button in the list: the approver now
 * opens the content, looks at it, and makes the decision from this bar. Approve
 * routes through a confirmation/attestation step (feeding the Part 11 e-signature
 * record); Deny requires a reason. Both return to the originating queue/list.
 *
 * Props:
 *   entityType - 'assessment' | 'study'
 *   ident      - approve/deny key: assessment uuid, or study-draft id
 *   entity     - optional entity object for display (name, submitter, date) + history id
 *   backTo     - route to return to after a decision (default per entity type)
 */
const REVIEW_API = {
  assessment: { approve: approveAssessment, deny: denyAssessment },
  study: { approve: approveStudyDraft, deny: denyStudyDraft },
};

const DEFAULT_BACK = {
  assessment: '/assessment-builder/review-queue',
  study: '/studybuilder/drafts',
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.reason || fallback;

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ReviewActionBar = ({ entityType = 'assessment', ident, entity, backTo }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // The dev/uat/demo EnvironmentBanner is fixed to bottom-0 at a high z-index;
  // float this bar just above it (offset = 0 in prod where the banner is absent).
  const [bottomOffset, setBottomOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      const banner = document.querySelector('.env-dev-banner');
      setBottomOffset(banner ? banner.getBoundingClientRect().height : 0);
    };
    measure();
    // Re-measure shortly after mount (banner animates in) and on resize.
    const t = setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, []);

  const api = REVIEW_API[entityType] || REVIEW_API.assessment;
  const back = backTo || DEFAULT_BACK[entityType] || '/';

  const name = entity?.name || entity?.survey?.name || 'this item';
  const submitter = entity?.submitted_by_email
    || (entity?.submitted_by && typeof entity.submitted_by === 'object' ? entity.submitted_by.email : null);
  const submittedAt = fmtDate(entity?.submitted_at);

  const goBack = () => navigate(back);

  const confirmApprove = async () => {
    setBusy(true);
    try {
      await api.approve(ident);
      toast.success('Approved.');
      setApproveOpen(false);
      goBack();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not approve.'));
      setBusy(false);
    }
  };

  const confirmDeny = async () => {
    if (!denyReason.trim()) return;
    setBusy(true);
    try {
      await api.deny(ident, denyReason.trim());
      toast.success('Changes requested.');
      setDenyOpen(false);
      goBack();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not deny.'));
      setBusy(false);
    }
  };

  if (!ident) return null;

  return (
    <>
      {/* Spacer so page content can scroll clear of the fixed bar + env banner */}
      <div aria-hidden="true" style={{ height: 80 + bottomOffset }} />

      <div
        className="fixed left-0 right-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
        style={{ bottom: bottomOffset }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={goBack}
              className="flex-shrink-0 flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
              title="Back to review queue"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-blue-700 dark:text-blue-300 flex-shrink-0">
              <ClipboardCheck className="w-5 h-5" />
              <span className="text-sm font-semibold">Reviewing</span>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{name}</div>
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                {submitter && (
                  <span className="flex items-center gap-1 truncate">
                    <User className="w-3.5 h-3.5" /> {submitter}
                  </span>
                )}
                {submittedAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {submittedAt}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {entity?.id != null && (
              <button
                onClick={() => setHistoryOpen(true)}
                className="px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                title="Review history"
              >
                <History className="w-4 h-4" />
                <span className="hidden md:inline">History</span>
              </button>
            )}
            <button
              onClick={() => setDenyOpen(true)}
              disabled={busy}
              className="px-4 py-1.5 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              <span>Deny</span>
            </button>
            <button
              onClick={() => setApproveOpen(true)}
              disabled={busy}
              className="px-5 py-1.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              <span>Approve</span>
            </button>
          </div>
        </div>
      </div>

      {historyOpen && (
        <ReviewHistory
          entityType={entityType}
          objectId={entity?.id}
          entityName={name}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* Approve confirmation / attestation */}
      {approveOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setApproveOpen(false); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-4 flex items-start">
              <div className="flex-shrink-0 bg-teal-100 rounded-full p-3">
                <Check className="w-6 h-6 text-teal-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Approve “{name}”?</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  This records your approval with your name and a timestamp, and clears it to be published. The submitter will be notified.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setApproveOpen(false)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Processing…' : 'Confirm approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deny reason modal */}
      {denyOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setDenyOpen(false); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-4 flex items-start">
              <div className="flex-shrink-0 bg-red-100 rounded-full p-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Request changes</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  The submitter will be notified with your reason. This is required.
                </p>
              </div>
            </div>
            <div className="px-6 pb-2">
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Explain what needs to change before this can be approved…"
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
              />
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setDenyOpen(false)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeny}
                disabled={busy || !denyReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? 'Processing…' : 'Request changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewActionBar;
